import { Suspense, lazy } from "react";
import FXBackdrop from "../components/FXBackdrop";
import PageTransition from "../components/PageTransition";
import AnimatedCounter from "../components/AnimatedCounter";
import ScrollDepth from "../components/ScrollDepth";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, ShieldCheck, Sparkles, CalendarClock, ScanSearch, GitMerge, BarChart3 } from "lucide-react";
import { NavLink } from "react-router-dom";
import SmartHireCTA from "../components/SmartHireCTA";

const HeroScene3D = lazy(() => import("../components/HeroScene3D"));

export default function Home() {
  return (
    <PageTransition>
      <FXBackdrop>

        {/* ═══════════════ HERO ═══════════════ */}
        <section className="containerX pt-16 md:pt-24 pb-16 md:pb-20 relative">
          {/* floating decorative shapes */}
          <div className="float-shape bg-violet-400 h-16 w-16 -top-4 right-20 hidden lg:block" style={{ animationDelay: "0s" }} />
          <div className="float-shape-slow bg-purple-400 h-10 w-10 top-40 -left-6 hidden lg:block" style={{ animationDelay: "2s" }} />
          <div className="float-shape bg-violet-300 h-8 w-8 bottom-10 right-10 hidden lg:block" style={{ animationDelay: "1s" }} />

          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              className="flex flex-col items-start text-left"
            >
              <motion.div
                className="chip"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1, duration: 0.4 }}
              >
                <span className="h-2 w-2 rounded-full" style={{ background: "rgb(139,92,246)" }} />
                BUILDING THE FUTURE OF HIRING
              </motion.div>

              <motion.h1
                className="h1 mt-6 max-w-2xl"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.7 }}
              >
                Vetted South Asian engineers for US startups.{" "}
                <br />
                <span className="text-black">
                  First candidates in 72 hours.
                </span>
              </motion.h1>

              <motion.p
                className="p mt-5 max-w-xl text-lg"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35, duration: 0.6 }}
              >
                Not just recruiting. We're a network. We source, vet, and deliver
                high signal candidates with scorecards and a shortlist your team can trust.
              </motion.p>

              <motion.div
                className="mt-8 flex flex-wrap gap-3"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
              >
                <SmartHireCTA label="Get a shortlist" className="btn btnPrimary px-7 pulse-glow" />
              </motion.div>

              <motion.div
                className="mt-4 flex items-center gap-2 text-sm text-slate-500"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.65, duration: 0.5 }}
              >
                <ShieldCheck size={15} className="text-violet-500 flex-shrink-0" />
                <span>90-day replacement guarantee on every placement</span>
              </motion.div>
            </motion.div>

            {/* 3D Network Globe */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, delay: 0.3, ease: "easeOut" }}
              className="hidden md:flex items-center justify-center"
            >
              <Suspense fallback={
                <div className="w-full h-[420px] flex items-center justify-center">
                  <div className="h-48 w-48 rounded-full animate-pulse" style={{ background: "radial-gradient(circle, rgba(139,92,246,0.2), transparent 70%)" }} />
                </div>
              }>
                <HeroScene3D />
              </Suspense>
            </motion.div>
          </div>
        </section>

        {/* ═══════════════ WHAT YOU RECEIVE ═══════════════ */}
        <section className="containerX pb-10">
          <motion.div
            className="glass p-6 md:p-8"
            whileHover={{ scale: 1.01 }}
            transition={{ duration: 0.3 }}
          >
            <div className="text-sm font-semibold mb-1">What you receive</div>
            <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                ["Role intake", "We define must-haves, dealbreakers, and scorecards."],
                ["Vetting", "Communication + skills + role-fit screening."],
                ["Shortlist", "3 to 7 candidates with strengths, risks, and notes."],
                ["Interview support", "Scheduling + feedback loop + offer close."],
              ].map(([t, d], i) => (
                <motion.div
                  key={t}
                  className="rounded-2xl border p-4"
                  style={{ borderColor: "rgba(226,232,240,0.9)", background: "rgba(255,255,255,0.5)" }}
                  whileHover={{ y: -6, scale: 1.03, boxShadow: "0 16px 40px rgba(139,92,246,0.12)", borderColor: "rgba(139,92,246,0.3)" }}
                  whileTap={{ scale: 0.97 }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                >
                  <div className="font-medium">{t}</div>
                  <div className="p mt-1 text-sm">{d}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>

        {/* ═══════════════ GUARANTEE ═══════════════ */}
        <section className="containerX pb-10">
          <motion.div
            className="glass p-6 md:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            whileHover={{ y: -4, boxShadow: "0 20px 50px rgba(139,92,246,0.12)" }}
            transition={{ duration: 0.5 }}
          >
            <div className="h-14 w-14 rounded-2xl bg-violet-100 flex items-center justify-center shrink-0">
              <ShieldCheck size={26} className="text-violet-600" />
            </div>
            <div>
              <div className="font-semibold text-slate-900 text-lg">90-day replacement guarantee</div>
              <p className="p mt-1 max-w-2xl text-sm">
                If a hire doesn't work out within the first 90 days for any performance-related reason,
                we replace them at no additional placement fee. No other agency in this space offers this.
              </p>
            </div>
          </motion.div>
        </section>

        {/* ═══════════════ STATS — animated counters ═══════════════ */}
        <ScrollDepth depth="recede" intensity={0.6}>
          <section className="dark-section py-10 relative overflow-hidden">
            {/* floating accent */}
            <div className="float-shape-slow bg-violet-500 h-32 w-32 -top-10 -right-10" style={{ animationDelay: "1.5s" }} />

            <div className="containerX">
              <div className="grid md:grid-cols-3 gap-4">
                {[
                  { value: "72", suffix: "h", desc: "Average time to first qualified matches" },
                  { value: "3", suffix: " to 7", desc: "Typical curated shortlist size" },
                  { value: "High-signal", suffix: "", desc: "Scorecards + screening notes included" },
                ].map((s, i) => (
                  <div key={i} className="glass-dark p-6">
                    <div className="text-3xl font-semibold">
                      <AnimatedCounter value={s.value} suffix={s.suffix} />
                    </div>
                    <div className="p mt-2">{s.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </ScrollDepth>

        {/* ═══════════════ HOW IT WORKS ═══════════════ */}
        <ScrollDepth depth="recede" intensity={0.4}>
          <section className="dark-section py-12 md:py-16 relative overflow-hidden">
            <div className="float-shape bg-purple-500 h-20 w-20 bottom-10 -left-8" style={{ animationDelay: "0.5s" }} />

            <div className="containerX">
              <div className="chip">Process</div>
              <h2 className="h2 mt-4">A simple pipeline. No noise.</h2>
              <p className="p mt-3 max-w-2xl">
                Your team talks to fewer people, but better people. Every candidate comes with clear evaluation.
              </p>

              <div className="mt-8 grid md:grid-cols-2 gap-4">
                {[
                  ["01", "Intake & scorecard", "Define must-haves, nice-to-haves, and rubric."],
                  ["02", "Sourcing + screening", "Skills + communication + role fit checks."],
                  ["03", "Shortlist + interviews", "Interview the best; we manage the loop."],
                  ["04", "Offer + close", "Offer support through negotiation and close."],
                ].map(([n, t, d], i) => (
                  <motion.div
                    key={n} className="glass-dark p-6 group"
                    whileHover={{ y: -5, scale: 1.02, boxShadow: "0 18px 44px rgba(139,92,246,0.15)" }}
                    whileTap={{ scale: 0.97 }}
                    transition={{ duration: 0.15, ease: "easeOut" }}
                  >
                    <div className="flex items-center gap-3">
                      <motion.div
                        className="h-10 w-10 rounded-2xl flex items-center justify-center font-semibold"
                        style={{ background: "rgba(139,92,246,0.15)", border: "1px solid rgba(139,92,246,0.3)" }}
                        whileHover={{ scale: 1.2, rotate: 6 }}
                        transition={{ type: "spring", stiffness: 400 }}
                      >
                        {n}
                      </motion.div>
                      <div className="font-semibold">{t}</div>
                    </div>
                    <p className="p mt-3">{d}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        </ScrollDepth>

        {/* ═══════════════ SERVICES ═══════════════ */}
        <ScrollDepth depth="lift" intensity={0.5}>
          <section className="containerX py-12 md:py-16 relative">
            <div className="float-shape bg-violet-400 h-14 w-14 top-0 right-8 hidden lg:block" style={{ animationDelay: "3s" }} />

            <div className="chip">Services</div>
            <h2 className="h2 mt-4">Built for modern hiring</h2>
            <p className="p mt-3 max-w-2xl">
              Pick what you need: full-cycle recruiting, shortlist-only, or pipeline building.
            </p>

            <div className="mt-8 grid md:grid-cols-3 gap-4">
              {[
                ["Shortlist Sprint", ["72h first candidates", "Scorecards included", "Fixed timeline"]],
                ["Full-Cycle Hiring", ["Sourcing → close", "Interview loop management", "Offer support"]],
                ["Talent Pipeline", ["Build bench for roles", "Monthly sourcing", "Market insights"]],
              ].map(([title, bullets], i) => (
                <motion.div
                  key={title} className="glass p-6"
                  whileHover={{ y: -8, scale: 1.03, boxShadow: "0 24px 60px rgba(139,92,246,0.14)" }}
                  whileTap={{ scale: 0.97 }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                >
                  <div className="font-semibold text-lg">{title}</div>
                  <div className="mt-4 grid gap-2">
                    {bullets.map((b) => (
                      <motion.div key={b} className="flex gap-2 items-start text-sm" whileHover={{ x: 4 }} transition={{ type: "spring", stiffness: 400 }}>
                        <CheckCircle2 size={16} className="mt-0.5 text-violet-500" />
                        <span className="text-slate-600">{b}</span>
                      </motion.div>
                    ))}
                  </div>
                  <NavLink to="/contact" className="btn btnGhost mt-6 w-full">Request details</NavLink>
                </motion.div>
              ))}
            </div>
          </section>
        </ScrollDepth>

        {/* ═══════════════ TESTIMONIALS ═══════════════ */}
        {/* <ScrollDepth depth="recede" intensity={0.4}>
          <section className="dark-section py-12 md:py-16 relative overflow-hidden">
            <div className="float-shape-slow bg-violet-400 h-24 w-24 -top-6 right-20" style={{ animationDelay: "2s" }} />

            <div className="containerX">
              <div className="chip">Testimonials</div>
              <h2 className="h2 mt-4">Hiring that people actually like</h2>
              <p className="p mt-3 max-w-2xl">
                Don't take our word for it. Here's what our clients and candidates say.
              </p>

              <div className="mt-8 grid md:grid-cols-3 gap-4">
                {[
                  {
                    quote: "We got a shortlist in 4 days and closed the hire in 2 weeks. Best signal-to-noise we've had.",
                    name: "Alex T.",
                    title: "Founder, Seed-stage startup",
                  },
                  {
                    quote: "Finally a recruiting process that feels measurable. Scorecards + notes saved our team hours.",
                    name: "Priya S.",
                    title: "Head of Engineering",
                  },
                  {
                    quote: "As a candidate, the feedback loop was actually real. Clear timeline, no guessing.",
                    name: "Marcus L.",
                    title: "Senior Engineer",
                  },
                ].map((t, i) => (
                  <motion.div
                    key={i} className="glass-dark p-6"
                    whileHover={{ y: -7, scale: 1.03, boxShadow: "0 20px 50px rgba(139,92,246,0.16)" }}
                    whileTap={{ scale: 0.97 }}
                    transition={{ type: "spring", stiffness: 280, damping: 22 }}
                  >
                    {/* star rating */}
                    {/* <div className="flex gap-0.5 mb-3">
                      {[...Array(5)].map((_, j) => (
                        <motion.svg key={j} width="14" height="14" viewBox="0 0 20 20" fill="rgba(250,204,21,0.9)"
                          initial={{ opacity: 0, scale: 0 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: false, amount: 0.1 }}
                          transition={{ delay: 0.3 + i * 0.1 + j * 0.06, duration: 0.3 }}
                        >
                          <path d="M10 1l2.39 4.84 5.34.78-3.87 3.77.91 5.32L10 13.27l-4.77 2.51.91-5.32L2.27 6.69l5.34-.78L10 1z" />
                        </motion.svg>
                      ))}
                    </div>

                    <p className="text-white/90 leading-relaxed">"{t.quote}"</p>

                    <div className="mt-4 flex items-center gap-3">
                      <motion.div
                        className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold"
                        style={{ background: "rgba(139,92,246,0.2)", color: "rgba(139,92,246,1)" }}
                        whileHover={{ scale: 1.15 }}
                      >
                        {t.name[0]}
                      </motion.div>
                      <div>
                        <div className="text-sm font-medium text-white/90">{t.name}</div>
                        <div className="text-xs" style={{ color: "rgba(166,178,206,0.8)" }}>{t.title}</div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        </ScrollDepth>  */}

        {/* ═══════════════ AI VETTING & MATCHMAKING ═══════════════ */}
        <ScrollDepth depth="lift" intensity={0.5}>
          <section className="containerX py-12 md:py-16 relative">
            <div className="float-shape bg-violet-400 h-14 w-14 top-2 right-10 hidden lg:block" style={{ animationDelay: "1s" }} />

            <div className="flex items-center gap-3 flex-wrap">
              <div className="chip"><Sparkles size={14} /> AI-Powered</div>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
                style={{ background: "rgba(139,92,246,0.1)", border: "1px solid rgba(139,92,246,0.3)", color: "rgb(109,40,217)" }}>
                <CalendarClock size={12} /> Coming Soon
              </span>
            </div>

            <h2 className="h2 mt-4">AI Vetting and Matchmaking</h2>
            <p className="p mt-3 max-w-2xl">Coming Soon</p>

            <div className="mt-8 grid md:grid-cols-3 gap-4">
              {[
                [ScanSearch, "AI Resume Screening", "Automated skill extraction and role-fit scoring across every application, at scale."],
                [GitMerge, "Precision Matching", "Candidates ranked by compatibility with your stack, culture, and hiring rubric."],
                [BarChart3, "Retention Scoring", "Predict long-term fit before the first interview using behavioural and experience signals."],
              ].map(([Icon, title, desc]) => (
                <motion.div
                  key={title}
                  className="glass p-6"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  whileHover={{ y: -8, scale: 1.03, boxShadow: "0 24px 60px rgba(139,92,246,0.14)" }}
                  whileTap={{ scale: 0.97 }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                >
                  <motion.div
                    className="inline-block"
                    whileHover={{ rotate: 12, scale: 1.15 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <Icon size={18} className="text-violet-500" />
                  </motion.div>
                  <div className="font-semibold text-lg mt-3">{title}</div>
                  <div className="p mt-2 text-sm">{desc}</div>
                </motion.div>
              ))}
            </div>
          </section>
        </ScrollDepth>

        {/* ═══════════════ FINAL CTA ═══════════════ */}
        <ScrollDepth depth="lift" intensity={0.6}>
          <section className="dark-section py-16">
            <div className="containerX">
            <motion.div
              className="glass-dark p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
              whileHover={{ scale: 1.015, boxShadow: "0 28px 70px rgba(139,92,246,0.18)" }}
              transition={{ duration: 0.35 }}
            >
              <div>
                <div className="chip">Next step</div>
                <div className="h2 mt-3">Tell us your role. We'll send matches.</div>
                <p className="p mt-2 max-w-2xl">We respond with a plan + your first candidate ETA.</p>
              </div>
              <NavLink to="/contact" className="btn btnPrimary pulse-glow">Book a call <ArrowRight size={16} /></NavLink>
            </motion.div>
            </div>
          </section>
        </ScrollDepth>

      </FXBackdrop>
    </PageTransition>
  );
}
